import { useEffect, useRef, useState, type ReactNode } from "react";
import { Lock, Undo2, X } from "lucide-react";
import { COLUMNAS, ETIQUETA_COLUMNA, fechaCorta, MOTIVO_OTRO, MOTIVOS_DESCARTE_CLIENTE } from "@/reclutamiento/logica";
import type { Columna, Etapa } from "@/reclutamiento/tipos";
import { IconoEtapa, Mk } from "./iconos";
import { claseEtapa } from "./etapas";
import { usePortal } from "./contexto";
import { MENSAJE_SIN_CONEXION } from "./red";

/** Hoja inferior en celular, diálogo centrado en pantallas anchas. Esc y el fondo cierran. */
export function Hoja({ titulo, onCerrar, children }: { titulo: string; onCerrar: () => void; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const antes = document.activeElement as HTMLElement | null;
    const p = panel.current;
    const primero = p?.querySelector<HTMLElement>("textarea, input, [data-autofocus], .optlist button, button:not(.icon-btn)");
    (primero ?? p)?.focus();
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCerrar();
      if (e.key === "Tab" && p) {
        const f = Array.from(p.querySelectorAll<HTMLElement>("button, a[href], textarea, input, select")).filter((x) => !x.hasAttribute("disabled"));
        if (!f.length) return;
        const [a, z] = [f[0], f[f.length - 1]];
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener("keydown", tecla);
    const ov = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", tecla);
      document.body.style.overflow = ov;
      antes?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="hoja">
      <button className="hoja-bg" aria-label="Cerrar" tabIndex={-1} onClick={onCerrar} />
      <div className="hoja-panel" role="dialog" aria-modal="true" aria-label={titulo} ref={panel} tabIndex={-1}>
        <div className="handle" />
        <div className="hoja-h">
          <h2>{titulo}</h2>
          <button className="icon-btn" onClick={onCerrar} aria-label="Cerrar">
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Hoja «Cambiar etapa» (antes «Mover a…»): columnas activas, Contratado y Algún día / Tal vez; «Descartar…» pide motivo; si lo descartó el cliente, «Regresar a activos». */
export function HojaMover({
  nombre,
  actual,
  etapa,
  onElegir,
  onDescartar,
  onRegresar,
  onCerrar,
}: {
  nombre: string;
  actual: Columna;
  etapa: Etapa;
  onElegir: (c: Columna) => void;
  onDescartar: () => void;
  onRegresar: () => void;
  onCerrar: () => void;
}) {
  return (
    <Hoja titulo={`Cambiar etapa de ${nombre}`} onCerrar={onCerrar}>
      <p className="hoja-sub">Elige en qué etapa va ahora.</p>
      <div className="optlist" role="group" aria-label="Columnas">
        {etapa === "descartado" && (
          <button
            data-autofocus
            onClick={() => {
              onCerrar();
              onRegresar();
            }}
          >
            <Undo2 size={16} aria-hidden="true" />
            Regresar a activos
          </button>
        )}
        {COLUMNAS.filter((c) => c !== "descartado").map((c) => {
          return (
            <button
              key={c}
              className={claseEtapa(c)}
              aria-pressed={c === actual}
              onClick={() => {
                onCerrar();
                if (c !== actual) onElegir(c);
              }}
            >
              <IconoEtapa c={c} s={30} />
              {ETIQUETA_COLUMNA[c]}
              {c === actual && <small>aquí está</small>}
            </button>
          );
        })}
        <button
          className={"descartar " + claseEtapa("descartado")}
          aria-pressed={actual === "descartado"}
          onClick={() => {
            onCerrar();
            onDescartar();
          }}
        >
          <IconoEtapa c="descartado" s={30} />
          {etapa === "descartado" ? "Cambiar motivo de descarte…" : "Descartar…"}
          {actual === "descartado" && <small>aquí está</small>}
        </button>
      </div>
    </Hoja>
  );
}

/** Motivo para descartar: los 5 de siempre u «Otro» con texto. */
export function HojaDescartar({
  nombre,
  inicial,
  onConfirmar,
  onCerrar,
}: {
  nombre: string;
  inicial?: string | null;
  onConfirmar: (motivo: string) => void;
  onCerrar: () => void;
}) {
  const esFijo = (m: string | null | undefined) => !!m && (MOTIVOS_DESCARTE_CLIENTE as readonly string[]).includes(m);
  const [elegido, setElegido] = useState<string | null>(esFijo(inicial) ? inicial! : inicial ? MOTIVO_OTRO : null);
  const [otro, setOtro] = useState(inicial && !esFijo(inicial) ? inicial.replace(/^Otro:\s*/i, "") : "");
  const motivo = elegido === MOTIVO_OTRO ? (otro.trim() ? `${MOTIVO_OTRO}: ${otro.trim()}` : "") : (elegido ?? "");
  return (
    <Hoja titulo={`¿Por qué descartas a ${nombre}?`} onCerrar={onCerrar}>
      <div className="optlist" role="radiogroup" aria-label="Motivo">
        {[...MOTIVOS_DESCARTE_CLIENTE, MOTIVO_OTRO].map((m) => (
          <button key={m} role="radio" aria-checked={elegido === m} aria-pressed={elegido === m} onClick={() => setElegido(m)}>
            <span className={"radio" + (elegido === m ? " on" : "")} aria-hidden="true" />
            {m}
          </button>
        ))}
      </div>
      {elegido === MOTIVO_OTRO && (
        <>
          <label className="sr" htmlFor="motivo-otro">
            Escribe el motivo
          </label>
          <input
            id="motivo-otro"
            className="inp"
            style={{ marginTop: 8 }}
            maxLength={280}
            autoFocus
            value={otro}
            onChange={(e) => setOtro(e.target.value)}
            placeholder="Escribe el motivo"
          />
        </>
      )}
      <p className="muted" style={{ fontSize: 13, margin: "10px 0 0" }}>
        Pasa a Descartados. Lo puedes regresar a activos cuando quieras.
      </p>
      <div className="row" style={{ marginTop: 10, justifyContent: "flex-end" }}>
        <button className="btn ghost" onClick={onCerrar}>
          Cancelar
        </button>
        <button
          className="btn primary"
          disabled={!motivo}
          onClick={() => {
            onCerrar();
            onConfirmar(motivo);
          }}
        >
          Descartar
        </button>
      </div>
    </Hoja>
  );
}

/** Estado del pago del periodo, arriba de Inicio y Cuenta. */
export function BannerPago({ conBoton = true, monto }: { conBoton?: boolean; monto?: string | null }) {
  const { sesion } = usePortal();
  if (sesion.bloqueado)
    return (
      <div className="banner yel" role="status">
        <Lock size={20} aria-hidden="true" />
        <div className="bt">
          <b>Pago pendiente{monto ? `: ${monto}` : ""}</b>
          {sesion.pagado_hasta ? `Tu periodo venció el ${fechaCorta(sesion.pagado_hasta)}. ` : ""}
          Ves tus números, y las fichas se liberan al confirmar tu pago.
        </div>
        {conBoton && (
          <a className="mini" href="#/cuenta">
            Cómo pagar
          </a>
        )}
      </div>
    );
  if (!sesion.pagado_hasta) return null;
  return (
    <div className="banner ok" role="status">
      <Mk k="ok" s={20} />
      <div className="bt">
        <b>Pagado hasta el {fechaCorta(sesion.pagado_hasta)}</b>
        Gracias. Todas tus fichas están abiertas.
      </div>
    </div>
  );
}

export function Esqueleto({ filas = 3, alto = 120 }: { filas?: number; alto?: number }) {
  return (
    <div aria-busy="true" aria-label="Cargando" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: filas }, (_, i) => (
        <div key={i} className="sk" style={{ height: alto }} />
      ))}
    </div>
  );
}

export function CajaError({ texto, onReintentar }: { texto: string; onReintentar?: () => void }) {
  // Sin conexión no es un error de la persona ni del sistema: aviso tranquilo (y se sigue intentando solo).
  const red = texto === MENSAJE_SIN_CONEXION;
  return (
    <div className={"errbox" + (red ? " suave" : "")} role={red ? "status" : "alert"}>
      <b>{texto}</b>
      {onReintentar && (
        <button className="btn" onClick={onReintentar}>
          {red ? "Reintentar ahora" : "Reintentar"}
        </button>
      )}
    </div>
  );
}
